import { useDonorQuery } from "../../lib/api/donors";
import { formatMoney } from "../../lib/format/money";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { MetricCard } from "../../components/ui/MetricCard";
import { Modal } from "../../components/ui/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/ui/Table";

interface DonorDetailModalProps {
  donorId: string | null;
  onClose: () => void;
  canManage: boolean;
  onAddFund: () => void;
  onAddPledge: () => void;
}

export function DonorDetailModal({
  donorId,
  onClose,
  canManage,
  onAddFund,
  onAddPledge,
}: DonorDetailModalProps) {
  const donor = useDonorQuery(donorId);

  return (
    <Modal
      open={Boolean(donorId)}
      onClose={onClose}
      title={donor.data?.donor.name ?? "Donor details"}
      description={donor.data?.donor.type}
      footer={
        <>
          {canManage ? (
            <>
              <Button variant="secondary" onClick={onAddFund}>
                Add fund
              </Button>
              <Button variant="secondary" onClick={onAddPledge}>
                Record pledge
              </Button>
            </>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {donor.isLoading ? (
        <LoadingSpinner label="Loading donor…" className="py-10" />
      ) : donor.data ? (
        <div className="grid gap-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Pledge" value={formatMoney(donor.data.donor.summary.pledge)} />
            <MetricCard label="Received" value={formatMoney(donor.data.donor.summary.received)} />
            <MetricCard
              label="Outstanding pledge"
              value={formatMoney(donor.data.donor.summary.outstandingPledge)}
            />
            <MetricCard label="Unspent" value={formatMoney(donor.data.donor.summary.unspent)} />
          </div>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-edlab-ink">Funds</h3>
            <div className="grid gap-3">
              {donor.data.donor.funds.map((fund) => (
                <div
                  key={fund.id}
                  className="rounded-xl border border-edlab-line bg-edlab-bg/40 px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-edlab-ink">{fund.name}</p>
                      <p className="text-xs text-edlab-muted">
                        {fund.restricted ? "Restricted" : "Unrestricted"} ·{" "}
                        {fund.unitIds.length} units
                      </p>
                    </div>
                    <Badge tone="neutral">
                      {`${formatMoney(fund.metrics.availableToCommit)} available`}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {donor.data.donor.pledges.length ? (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-edlab-ink">Pledges</h3>
              <Table caption="Donor pledges">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Fund</TableHeaderCell>
                    <TableHeaderCell>Period</TableHeaderCell>
                    <TableHeaderCell>Amount</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {donor.data.donor.pledges.map((pledge) => (
                    <TableRow key={pledge.id}>
                      <TableCell>{pledge.fundName}</TableCell>
                      <TableCell>{pledge.period}</TableCell>
                      <TableCell>{formatMoney(pledge.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>
          ) : null}

          {donor.data.donor.expenses.length ? (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-edlab-ink">Linked expenses</h3>
              <Table caption="Donor fund expenses">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Reference</TableHeaderCell>
                    <TableHeaderCell>Payee</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Paid</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {donor.data.donor.expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{expense.referenceNo}</TableCell>
                      <TableCell>{expense.payee}</TableCell>
                      <TableCell>{expense.status}</TableCell>
                      <TableCell>
                        {formatMoney(expense.paidAmount)} / {formatMoney(expense.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
